import {
  useImageCrop,
  type Point,
} from "@labeling-tools/ui-components/src/hooks/useImageCrop";
import { useEffect, useState } from "react";
import { auth } from "@/utils/auth";

const base64ToUint8Array = (base64String: string) => {
  const binaryString = atob(base64String);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

export const useEmbedding = (missingItemImageUrl: string, points: Point[]) => {
  const cropResult = useImageCrop(missingItemImageUrl || "", [], points, true);
  const [embedding, setEmbedding] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = auth.currentUser;

  /**
   * Get the embedding for the cropped image
   */
  const getEmbedding = async () => {
    if (cropResult.croppedImageUrl) {
      let imageBytes;
      try {
        imageBytes = base64ToUint8Array(
          cropResult.croppedImageUrl.split(",")[1],
        );
      } catch (error) {
        console.error("Error decoding Base64 string:", error);
        return; // Exit if decoding fails
      }
      const imageByteLength = imageBytes.length;

      // --- 3. PREPARE DATA FOR BATCH OF 1 ---
      // Convert Uint8Array to a plain array for JSON serialization
      const paddedData = Array.from(imageBytes);
      // Array of original lengths (only one image in this batch)
      const lengthsData = [imageByteLength];

      // --- 4. CREATE JSON PAYLOAD ---
      const requestBody = {
        inputs: [
          {
            name: "input", // From Python code
            shape: [1, imageByteLength], // [batch_size, max_len (which is imageByteLength for batch size 1)]
            datatype: "UINT8", // From Python code
            data: paddedData,
          },
          {
            name: "jpeg_lengths", // From Python code
            shape: [1, 1], // [batch_size, 1]
            datatype: "INT32", // From Python code
            data: lengthsData,
          },
        ],
        outputs: [
          {
            name: "embedding", // From Python code
          },
        ],
      };
      setLoading(true);
      setError(null);

      try {
        const token = await user?.getIdToken();
        fetch(
          `${import.meta.env.VITE_EMBEDDINGTON_BASE_URL}/v0/triton/create-embedding`,
          {
            method: "POST",
            body: JSON.stringify(requestBody),
            headers: {
              "Content-Type": "application/json",
              // 'Inference-Header-Content-Length': 270,
              Authorization: `Bearer ${token}`,
            },
          },
        )
          .then((response) => response.json())
          .then((data) => {
            // Extract the embedding from the response
            if (
              data.outputs &&
              data.outputs.length > 0 &&
              data.outputs[0].data
            ) {
              setEmbedding(data.outputs[0].data);
            } else {
              setError("No embedding data found in response");
            }
          })
          .catch((error) => {
            console.error("Error fetching embedding:", error);
            setError("Failed to fetch embedding");
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.error("Error fetching embedding:", error);
        setError("Failed to fetch embedding");
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    getEmbedding();
  }, [cropResult.croppedImageUrl]);

  return { embedding, loading, error };
};
